<!-- eslint-disable no-undef -->
<script setup>
import { ref, computed, watch } from 'vue';

const props = defineProps({
    title:     { type: String,  default: '' },
    jsonValue: { type: Object,  required: false, default: () => ({}) },
    schema:    { default: null },
    advanced:  { type: Boolean, default: false },
    readonly:  { type: Boolean, default: false },
});

const emit = defineEmits(['update:jsonValue', 'update:valid']);

// Snapshot at mount — never mutated, used only for reset
const originalValue = JSON.parse(JSON.stringify(props.jsonValue));

const editableValue       = ref(JSON.parse(JSON.stringify(props.jsonValue)));
const editableValueString = ref(
    Object.keys(props.jsonValue).length
        ? JSON.stringify(props.jsonValue, null, 2)
        : '{\n\n}'
);
const activeTab = ref('simple');
const validJson = ref(true);

const inputClass = 'flex-1 font-body border border-mc-steel dark:border-line-on-dark rounded-mc-sm bg-mc-white dark:bg-mc-charcoal px-3 py-1.5 text-sm text-mc-ink dark:text-on-dark transition-[border-color,box-shadow] duration-[160ms] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40 focus-visible:ring-offset-[3px] disabled:opacity-50 disabled:cursor-not-allowed';

// Sync when parent updates the value externally.
// Guard against the round-trip loop: emit → parent updates prop → this watcher fires → emit again.
// If the serialised value matches what we already hold, the parent is just reflecting our own emit back — skip it.
watch(() => props.jsonValue, (newValue) => {
    if (JSON.stringify(newValue) === JSON.stringify(editableValue.value)) return;
    editableValue.value       = JSON.parse(JSON.stringify(newValue));
    editableValueString.value = JSON.stringify(newValue, null, 2);
    validJson.value           = true;
}, { deep: true });

// Emit every time the working copy changes
watch(editableValue, (newValue) => {
    emit('update:jsonValue', newValue);
}, { deep: true });

watch(validJson, (v) => emit('update:valid', v));

// Split top-level keys into flat primitives vs. objects/arrays
const flatFields = computed(() =>
    Object.entries(editableValue.value).filter(
        ([, v]) => v === null || (typeof v !== 'object')
    )
);

const sectionFields = computed(() =>
    Object.entries(editableValue.value).filter(
        ([, v]) => v !== null && typeof v === 'object'
    )
);

const formatKey = (key) => {
    if (key === 'ssid') return 'Network Name';
    if (key === 'psk')  return 'Password';
    return String(key)
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase())
        .trim();
};

const getLabel = (key) => props.schema?.[key]?.placeholder || formatKey(key);

const updateValue = (value, path) => {
    try {
        let target = editableValue.value;
        const keys = path.split('.');
        const lastKey = keys.pop();
        for (const k of keys) {
            if (!(k in target)) return;
            target = target[k];
        }
        const originalType = typeof target[lastKey];
        if (originalType === 'number')  { target[lastKey] = Number(value); return; }
        if (originalType === 'boolean') { target[lastKey] = value === 'true'; return; }
        if (target[lastKey] === null)   { target[lastKey] = value || null; return; }
        target[lastKey] = value;
    } catch (error) {
        console.error('Error updating value:', error);
    }
};

const handleRawInput = (event) => {
    const raw = event.target.value;
    editableValueString.value = raw;
    try {
        editableValue.value = JSON.parse(raw);
        validJson.value = true;
    } catch {
        validJson.value = false;
    }
};

const handleRawKeydown = (event) => {
    if (event.key !== 'Tab') return;
    event.preventDefault();
    const el = event.target;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const indent = '  ';
    const newValue = el.value.substring(0, start) + indent + el.value.substring(end);
    el.value = newValue;
    el.selectionStart = el.selectionEnd = start + indent.length;
    editableValueString.value = newValue;
    try {
        editableValue.value = JSON.parse(newValue);
        validJson.value = true;
    } catch {
        validJson.value = false;
    }
};

const switchTab = (tab) => {
    if (tab === 'simple' && !validJson.value) return;
    if (tab === 'simple') {
        editableValueString.value = JSON.stringify(editableValue.value, null, 2);
    }
    activeTab.value = tab;
};

const resetToOriginal = () => {
    editableValue.value       = JSON.parse(JSON.stringify(originalValue));
    editableValueString.value = JSON.stringify(originalValue, null, 2);
    validJson.value = true;
    activeTab.value = 'simple';
};

const isObjectArray = (arr) =>
    Array.isArray(arr) &&
    arr.length > 0 &&
    arr.every(item => item !== null && typeof item === 'object' && !Array.isArray(item));

const addArrayItem = (key) => {
    const arr = editableValue.value[key];
    if (!Array.isArray(arr)) return;
    if (arr.length === 0) { arr.push({}); return; }
    const template = arr[0];
    const newItem = Object.fromEntries(
        Object.entries(template).map(([k, v]) => {
            if (v === null)            return [k, null];
            if (typeof v === 'boolean') return [k, false];
            if (typeof v === 'number')  return [k, 0];
            return [k, ''];
        })
    );
    arr.push(newItem);
};

const removeArrayItem = (key, index) => {
    const arr = editableValue.value[key];
    if (!Array.isArray(arr)) return;
    arr.splice(index, 1);
};
</script>

<template>
    <div>
        <!-- Header row -->
        <div class="flex items-center justify-between mb-4 min-h-[44px]">
            <span class="text-sm font-bold text-mc-ink dark:text-on-dark">{{ title }}</span>

            <div class="flex items-center gap-3">
                <!-- Reset (only when raw JSON is invalid) -->
                <button
                    v-if="!validJson"
                    type="button"
                    data-testid="reset-button"
                    aria-label="Reset to original value"
                    @click="resetToOriginal"
                    class="rounded-pill border-2 border-mc-ink dark:border-on-dark px-4 py-[0.55rem] text-xs font-bold font-body text-mc-ink dark:text-on-dark transition-colors duration-[160ms] hover:bg-mc-ink hover:text-mc-white dark:hover:bg-on-dark dark:hover:text-mc-ink min-h-[44px]"
                >
                    Reset
                </button>

                <!-- Segmented tab toggle -->
                <div
                    v-if="advanced && Object.keys(editableValue).length > 0"
                    class="inline-flex bg-mc-cloud dark:bg-mc-charcoal rounded-[10px] p-[3px] gap-[2px]"
                >
                    <button
                        type="button"
                        data-testid="tab-simple"
                        :aria-disabled="!validJson ? 'true' : 'false'"
                        :tabindex="!validJson ? -1 : 0"
                        @click="switchTab('simple')"
                        :class="[
                            'text-xs font-semibold px-3 rounded-[7px] border-none transition-all duration-[160ms] min-h-[44px]',
                            activeTab === 'simple'
                                ? 'bg-mc-white dark:bg-white/10 text-mc-ink dark:text-on-dark shadow-sm cursor-pointer'
                                : 'bg-transparent text-mc-ink/50 dark:text-on-dark-muted cursor-pointer',
                            !validJson ? 'opacity-40 !cursor-not-allowed' : '',
                        ]"
                    >
                        Simple
                    </button>
                    <button
                        type="button"
                        data-testid="tab-raw"
                        @click="switchTab('raw')"
                        :class="[
                            'text-xs font-semibold px-3 rounded-[7px] border-none cursor-pointer transition-all duration-[160ms] min-h-[44px]',
                            activeTab === 'raw'
                                ? 'bg-mc-white dark:bg-white/10 text-mc-ink dark:text-on-dark shadow-sm'
                                : 'bg-transparent text-mc-ink/50 dark:text-on-dark-muted',
                        ]"
                    >
                        Raw JSON
                    </button>
                </div>
            </div>
        </div>

        <!-- Simple view -->
        <div v-if="activeTab === 'simple'" data-testid="simple-view" class="flex flex-col gap-3">

            <!-- Flat fields (unlabeled card) -->
            <div
                v-if="flatFields.length"
                class="bg-mc-white dark:bg-mc-charcoal border border-mc-steel dark:border-line-on-dark rounded-mc-sm p-4 flex flex-col gap-2"
            >
                <div
                    v-for="[key, value] in flatFields"
                    :key="key"
                    class="flex items-center gap-3"
                >
                    <label
                        :for="`json-field-${key}`"
                        class="w-[130px] shrink-0 text-xs font-semibold text-mc-ink/70 dark:text-on-dark-muted"
                    >
                        {{ getLabel(key) }}
                    </label>
                    <select
                        v-if="Array.isArray(schema?.[key]?.enum)"
                        :id="`json-field-${key}`"
                        :value="value"
                        :disabled="readonly"
                        @change="updateValue($event.target.value, key)"
                        :class="inputClass"
                    >
                        <option v-for="opt in schema[key].enum" :key="opt" :value="opt">{{ opt }}</option>
                    </select>
                    <input
                        v-else
                        :id="`json-field-${key}`"
                        :value="value"
                        :type="typeof value === 'number' ? 'number' : 'text'"
                        :disabled="readonly"
                        @input="updateValue($event.target.value, key)"
                        :class="inputClass"
                    >
                </div>
            </div>

            <!-- Section cards (nested objects + arrays) -->
            <div
                v-for="[key, value] in sectionFields"
                :key="key"
                class="bg-mc-white dark:bg-mc-charcoal border border-mc-steel dark:border-line-on-dark rounded-mc-sm p-4"
            >
                <div class="text-xs font-extrabold text-mc-red uppercase tracking-[.1em] mb-3">
                    {{ formatKey(key) }}
                </div>

                <!-- Array of objects -->
                <template v-if="isObjectArray(value)">
                    <div
                        v-for="(item, index) in value"
                        :key="index"
                        class="border border-mc-steel dark:border-line-on-dark rounded-mc-sm p-3 mb-2"
                    >
                        <div class="flex items-center justify-between mb-2">
                            <span class="text-xs font-semibold text-mc-ink/50 dark:text-on-dark-muted">[{{ index }}]</span>
                            <button
                                v-if="!readonly"
                                type="button"
                                :disabled="readonly"
                                @click="removeArrayItem(key, index)"
                                class="text-xs font-semibold text-mc-red hover:text-mc-red/70 transition-colors duration-[160ms] disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Remove
                            </button>
                        </div>
                        <div
                            v-for="(fieldVal, fieldKey) in item"
                            :key="fieldKey"
                            class="flex items-center gap-3 mb-2 last:mb-0"
                        >
                            <label
                                :for="`json-field-${key}-${index}-${fieldKey}`"
                                class="w-[130px] shrink-0 text-xs font-semibold text-mc-ink/70 dark:text-on-dark-muted"
                            >
                                {{ formatKey(fieldKey) }}
                            </label>
                            <select
                                v-if="typeof fieldVal === 'boolean'"
                                :id="`json-field-${key}-${index}-${fieldKey}`"
                                :value="String(fieldVal)"
                                :disabled="readonly"
                                @change="updateValue($event.target.value, `${key}.${index}.${fieldKey}`)"
                                :class="inputClass"
                            >
                                <option value="true">true</option>
                                <option value="false">false</option>
                            </select>
                            <input
                                v-else
                                :id="`json-field-${key}-${index}-${fieldKey}`"
                                :value="fieldVal"
                                :type="typeof fieldVal === 'number' ? 'number' : 'text'"
                                :disabled="readonly"
                                @input="updateValue($event.target.value, `${key}.${index}.${fieldKey}`)"
                                :class="inputClass"
                            >
                        </div>
                    </div>
                    <button
                        v-if="!readonly"
                        type="button"
                        @click="addArrayItem(key)"
                        class="w-full mt-1 rounded-mc-sm border border-dashed border-mc-steel dark:border-line-on-dark text-xs font-semibold text-mc-ink/50 dark:text-on-dark-muted hover:border-mc-blue hover:text-mc-blue dark:hover:text-mc-blue transition-colors duration-[160ms] py-2"
                    >
                        + Add Item
                    </button>
                </template>

                <!-- Array of primitives -->
                <template v-else-if="Array.isArray(value)">
                    <div
                        v-for="(item, index) in value"
                        :key="index"
                        class="flex items-center gap-3 mb-2"
                    >
                        <label
                            :for="`json-field-${key}-${index}`"
                            class="w-[130px] shrink-0 text-xs font-semibold text-mc-ink/70 dark:text-on-dark-muted"
                        >
                            [{{ index }}]:
                        </label>
                        <input
                            :id="`json-field-${key}-${index}`"
                            :value="item"
                            :type="typeof item === 'number' ? 'number' : 'text'"
                            :disabled="readonly"
                            @input="updateValue($event.target.value, `${key}.${index}`)"
                            :class="inputClass"
                        >
                    </div>
                </template>

                <!-- Nested object fields -->
                <template v-else>
                    <div
                        v-for="(nestedValue, nestedKey) in value"
                        :key="nestedKey"
                        class="flex items-center gap-3 mb-2"
                    >
                        <label
                            :for="`json-field-${key}-${nestedKey}`"
                            class="w-[130px] shrink-0 text-xs font-semibold text-mc-ink/70 dark:text-on-dark-muted"
                        >
                            {{ formatKey(nestedKey) }}
                        </label>
                        <input
                            v-if="typeof nestedValue !== 'object'"
                            :id="`json-field-${key}-${nestedKey}`"
                            :value="nestedValue"
                            :type="typeof nestedValue === 'number' ? 'number' : 'text'"
                            :disabled="readonly"
                            @input="updateValue($event.target.value, `${key}.${nestedKey}`)"
                            :class="inputClass"
                        >
                        <span
                            v-else
                            class="text-xs text-mc-ink/40 dark:text-on-dark-muted italic"
                        >
                            [complex value]
                        </span>
                    </div>
                </template>
            </div>
        </div>

        <!-- Raw JSON view -->
        <div v-else class="relative">
            <textarea
                data-testid="raw-textarea"
                :value="editableValueString"
                @input="handleRawInput"
                @keydown="handleRawKeydown"
                aria-label="Raw JSON editor"
                :aria-invalid="!validJson"
                :disabled="readonly"
                class="w-full h-[300px] font-mono text-sm p-4 rounded-mc-sm border border-mc-steel dark:border-line-on-dark bg-mc-ink text-on-dark resize-y transition-[border-color,box-shadow] duration-[160ms] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40 focus-visible:ring-offset-[3px] disabled:opacity-50 disabled:cursor-not-allowed"
            ></textarea>

            <!-- Valid/Invalid indicator -->
            <div class="absolute bottom-3 right-3 flex items-center gap-1 text-xs font-semibold pointer-events-none select-none">
                <template v-if="validJson">
                    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" class="text-green-500 dark:text-green-400">
                        <circle cx="6" cy="6" r="6" fill="currentColor"/>
                        <path d="M3 6l2 2 4-4" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <span class="text-green-500 dark:text-green-400">Valid JSON</span>
                </template>
                <template v-else>
                    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" class="text-mc-red">
                        <circle cx="6" cy="6" r="6" fill="currentColor"/>
                        <path d="M4 4l4 4M8 4l-4 4" stroke="#fff" stroke-width="1.5" stroke-linecap="round"/>
                    </svg>
                    <span class="text-mc-red">Invalid JSON</span>
                </template>
            </div>
        </div>
    </div>
</template>
